import { differenceInCalendarDays, format, isValid, parseISO } from "date-fns";
import { statusTranslate } from "~/utils/advanceRequest";
import { GRANT_LABELS } from "~/auth/permissions";
import { getApiErrorCode, getApiErrorMessage } from "~/utils/apiError";

export const ROLE_OPTIONS = [
  { value: null, label: "Nenhum" },
  { value: "reader", label: "Leitura" },
  { value: "operator", label: "Operador" },
  { value: "admin", label: "Admin" },
];

// Cartões da ficha (v2-pessoa-ficha-padrao.png).
export const ROLE_CARDS = [
  { value: null, label: "Nenhum", description: "Sem área Antecipe; pode ser só gestor." },
  { value: "reader", label: "Leitura", description: "Consulta tudo, não altera nada." },
  { value: "operator", label: "Operador", description: "Opera pacotes, status, contratos e médicos." },
  { value: "admin", label: "Admin", description: "Operador + acessos, domínios e empresas." },
];

export const roleOptionLabel = (role) => ROLE_OPTIONS.find((o) => o.value === (role ?? null))?.label || "Nenhum";

export const userStatus = (user) => {
  if (user.status === "disabled") return { key: "disabled", label: "Desativado" };
  if (user.status === "invited") return user.invite?.expired ? { key: "expired", label: "Convite expirado" } : { key: "invited", label: "Convidado" };
  return { key: "active", label: "Ativo" };
};

export const STATUS_STYLES = {
  active: { bg: "#e8f5e9", fg: "#2e7d32" },
  invited: { bg: "#e1f5fe", fg: "#0277bd" },
  expired: { bg: "#fdecea", fg: "#d32f2f" },
  disabled: { bg: "#eceff1", fg: "#546e7a" },
};

// Motivos granulares do BE (spec §6.1). Os 4 primeiros têm texto no mockup; os demais: Apêndice.
export const DENIAL_REASON_LABELS = {
  not_invited: "Não convidado",
  invite_expired: "Convite expirado",
  invite_email_mismatch: "Conta Google diferente do convite",
  disabled: "Acesso desativado",
  invite_superseded: "Link de convite substituído",
  invite_revoked: "Convite cancelado",
  account_mismatch: "Conta Google diferente da cadastrada",
  email_unverified: "E-mail não verificado",
  no_permissions: "Sem permissões",
  oauth_error: "Erro no login Google",
  oauth_denied: "Login Google cancelado",
  state_mismatch: "Login expirado",
};
export const denialReasonLabel = (code) => DENIAL_REASON_LABELS[code] || code;

export const AUDIT_ACTION_LABELS = {
  "advance_payment.payment_date_changed": "Alterou data de pagamento",
  "advance_payment.status_changed": "Mudou status do pacote",
  "advance_payment.canceled": "Cancelou pacote",
  "advance_payment.merged": "Juntou pacotes",
  "advance_payment.completed_by_company": "Empresa enviou pacote",
  "vacancy_advance.updated": "Empresa conferiu plantão",
  "vacancy_advance.synced": "Sincronizou plantão",
  "doctor.bank_changed": "Alterou dados bancários do médico",
  "doctor.pj_changed": "Alterou dados de pessoa jurídica do médico",
  "doctor.bank_change_alert": "Alerta de troca bancária",
  "contract.downloaded": "Baixou contrato",
  "company.created": "Criou empresa",
  "company.updated": "Editou empresa",
  "company.deactivated": "Desativou empresa",
  "company.reactivated": "Reativou empresa",
  "external_system.created": "Criou sistema externo",
  "external_system.updated": "Editou sistema externo",
  "access.invite_created": "Convidou pessoa",
  "access.invite_resent": "Reenviou convite",
  "access.invite_link_copied": "Copiou link do convite",
  "access.invite_revoked": "Revogou convite",
  "access.invite_accepted": "Aceitou o convite e entrou pela 1ª vez",
  "access.preregistered": "Pré-cadastrou pessoa",
  "access.role_changed": "Alterou papel",
  "access.company_added": "Adicionou empresa",
  "access.company_removed": "Removeu empresa",
  "access.user_deactivated": "Desativou acesso",
  "access.user_reactivated": "Reativou acesso",
  "access.grants_changed": "Concessões alteradas",
  "domain.added": "Adicionou domínio",
  "domain.removed": "Removeu domínio",
};
// doctor.bank_change_alert (ator de sistema): o resultado do envio vem em metadata.alertEmailStatus
// ('sent' | 'failed:<código>' | 'no_admins'). Textos: Apêndice.
const bankChangeAlertLabel = (status) => {
  if (status === "sent") return "Alerta de troca bancária enviado";
  if (status === "no_admins") return "Alerta de troca bancária não enviado (sem Admin)";
  if (typeof status === "string" && status.startsWith("failed")) return "Alerta de troca bancária falhou";
  return AUDIT_ACTION_LABELS["doctor.bank_change_alert"];
};
export const auditActionLabel = (action, item) => {
  if (action === "doctor.bank_change_alert") return bankChangeAlertLabel(item?.metadata?.alertEmailStatus);
  return AUDIT_ACTION_LABELS[action] || action;
};
// Só marca a linha (ícone) e liga a máscara de conta/PIX com "mostrar" (spec §7). Não vira filtro.
export const SENSITIVE_ACTIONS = ["doctor.bank_changed", "doctor.pj_changed"];

// Coluna "Alvo" = targetLabel montado pelo BE no momento da ação (spec §7, P1-3). Sem heurística no FE;
// o fallback é o mesmo do BE para linhas antigas sem rótulo.
export const auditTargetLabel = (item) => item.targetLabel || `${item.entityType} #${item.entityId}`;

const MASKED_KEYS = ["account_number", "pix"];
const mask = (value) => {
  const text = String(value);
  return text.length <= 4 ? "••••" : `••••${text.slice(-4)}`;
};

// Status de pessoa nos payloads de auditoria com entityType "user" (access.user_deactivated/_reactivated,
// access.invite_accepted). Os demais status (pacote) seguem o dicionário do pacote. Textos: Apêndice.
export const USER_STATUS_LABELS = { active: "Ativo", disabled: "Desativado", invited: "Convidado" };
const companyLabel = (id, companiesById) => companiesById?.get(id) || `#${id}`;
const formatScalar = (v) => (typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v));

export const formatChangeValue = (value, { reveal = false, companiesById, entityType } = {}) => {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "object") return formatScalar(value);
  const parts = Object.entries(value)
    // isExternal falso não acrescenta nada; verdadeiro vira "E-mail externo" (B-11)
    .filter(([key, v]) => v !== null && v !== undefined && v !== "" && !(key === "isExternal" && !v))
    .map(([key, v]) => {
      if (key === "isExternal") return "E-mail externo";
      if (key === "role") return roleOptionLabel(v);
      if (key === "status") return entityType === "user" ? USER_STATUS_LABELS[v] || v : statusTranslate(v) || v;
      // access.grants_changed: { grants: [...] } (BE-0g)
      if (key === "grants" && Array.isArray(v)) return v.length ? v.map((g) => GRANT_LABELS[g] || g).join(", ") : "Nenhuma";
      if (key === "companyIds" && Array.isArray(v)) return v.map((id) => companyLabel(id, companiesById)).join(", ");
      // access.company_added / access.company_removed: { companyId } no singular (plano do BE).
      if (key === "companyId") return companyLabel(v, companiesById);
      if (!reveal && MASKED_KEYS.includes(key)) return mask(v);
      return Array.isArray(v) ? v.join(", ") : formatScalar(v);
    });
  return parts.length ? parts.join(" · ") : "—";
};

// Antes/depois de um AuditItem — único ponto usado pela tabela e pelo CSV da Auditoria e pelo histórico da ficha.
export const formatAuditChange = (item, options = {}) => {
  const opts = { ...options, entityType: item.entityType };
  return { before: formatChangeValue(item.before, opts), after: formatChangeValue(item.after, opts) };
};

const toDate = (iso) => {
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? date : null;
};
export const formatDateTimeBR = (iso) => {
  const d = toDate(iso);
  return d ? format(d, "dd/MM/yyyy HH:mm") : "—";
};
export const formatDateBR = (iso) => {
  const d = toDate(iso);
  return d ? format(d, "dd/MM/yyyy") : "—";
};
export const formatLastSeen = (iso, now = new Date()) => {
  const d = toDate(iso);
  if (!d) return "—";
  const diff = differenceInCalendarDays(now, d);
  if (diff === 0) return `Hoje, ${format(d, "HH:mm")}`;
  if (diff === 1) return `Ontem, ${format(d, "HH:mm")}`;
  return format(d, "dd/MM/yyyy");
};
export const daysUntil = (iso, now = new Date()) => {
  const d = toDate(iso);
  return d ? differenceInCalendarDays(d, now) : null;
};
// Histórico da ficha (v2-pessoa-ficha-padrao.png): "Hoje, 11:40" ou "05/10/2026 10:02".
export const formatWhen = (iso, now = new Date()) => {
  const d = toDate(iso);
  if (!d) return "—";
  return differenceInCalendarDays(now, d) === 0 ? `Hoje, ${format(d, "HH:mm")}` : format(d, "dd/MM/yyyy HH:mm");
};
export const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";
export const emailDomain = (email) => {
  const value = (email || "").trim().toLowerCase();
  const at = value.lastIndexOf("@");
  return at > 0 ? value.slice(at + 1) : "";
};

// invites.email_error do BE é um código classificado (nunca a mensagem crua do provedor):
// timeout | throttled | config | error:<id>. Textos: Apêndice.
export const inviteEmailErrorLabel = (code) => {
  if (!code) return null;
  if (code === "timeout") return "O serviço de e-mail não respondeu a tempo.";
  if (code === "throttled") return "Limite de envio de e-mails atingido. Tente de novo mais tarde.";
  if (code === "config") return "O envio de e-mail não está configurado neste ambiente.";
  if (code.startsWith("error:")) return `O serviço de e-mail recusou o envio (código ${code.slice(6) || "unknown"}).`;
  return "Erro desconhecido no envio do e-mail.";
};

// Textos sem mockup (P1-12): listados no Apêndice — Textos sem mockup (revisar no vídeo/demo).
const ACCESS_ERRORS = {
  STALE_ACCESS: "Alguém alterou este acesso antes de você. Recarregue e refaça a alteração.",
  LAST_ADMIN: "Não é possível ficar sem nenhum Admin ativo.",
  SELF_CHANGE_FORBIDDEN: "Você não pode alterar o próprio papel nem se desativar.",
  FORBIDDEN_ROLE: "Seu acesso mudou e não permite mais esta ação. Recarregue a tela.",
  USER_DISABLED: "Esta pessoa foi desativada e não pode receber alterações. Recarregue a tela.",
  USER_NOT_FOUND: "Uma das pessoas não existe mais. Recarregue a tela.",
  COMPANY_NOT_FOUND: "Uma das empresas não existe mais. Recarregue a tela.",
  COMPANY_INACTIVE: "Empresa inativa não recebe gestor novo. Reative a empresa em Empresas ou escolha outra.",
  USER_ALREADY_ACTIVE: "Esta pessoa já tem acesso. Edite o acesso pela matriz.",
  INVITE_ALREADY_PENDING: "Já existe um convite pendente para este e-mail.",
  INVITE_NOT_PENDING: "Este convite não está mais pendente. Recarregue a ficha.",
  INVITE_EXPIRED: "O convite expirou. Use Reenviar para gerar um novo link.",
  EXTERNAL_CONFIRMATION_REQUIRED: "Este e-mail é de fora do grupo. Confirme o convite externo para continuar.",
  NO_ACCESS_GRANTED: "Escolha um papel ou pelo menos uma empresa.",
  DOMAIN_EXISTS: "Este domínio já está na lista.",
  GRANT_REQUIRES_BACKOFFICE_ROLE: "Concessões só valem para quem tem papel de backoffice (Leitura, Operador ou Admin). Defina e salve um papel antes.",
};
export const accessErrorMessage = (error, fallback) => ACCESS_ERRORS[getApiErrorCode(error)] || getApiErrorMessage(error, fallback);
